import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Lock, AlertCircle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 px-0 py-3 text-ivory placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors text-base";
  const labelCls =
    "text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2 block";

  return (
    <div className="min-h-screen bg-obsidian text-ivory flex flex-col">
      <header className="px-6 lg:px-8 py-6">
        <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 group">
          <span className="font-display text-2xl tracking-tight text-ivory">
            Givance
          </span>
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-gold" />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4 flex items-center gap-3">
            <Lock className="w-3.5 h-3.5" />
            Admin Access
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl text-ivory leading-tight"
            data-testid="admin-login-heading"
          >
            Welcome back.
          </h1>
          <p className="mt-4 text-white/60 font-light">
            Log in to view and manage your booking enquiries.
          </p>

          <form
            onSubmit={submit}
            className="mt-12 space-y-8"
            data-testid="admin-login-form"
          >
            <div>
              <label className={labelCls} htmlFor="al-email">
                Email
              </label>
              <input
                id="al-email"
                data-testid="admin-login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@givance.co.uk"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="al-password">
                Password
              </label>
              <input
                id="al-password"
                data-testid="admin-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="flex items-start gap-3 p-4 border border-red-500/30 bg-red-500/5 text-red-200 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-ivory text-obsidian px-8 py-4 text-sm tracking-wide font-medium hover:bg-gold transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Signing in..." : "Sign in"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
